/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef } from 'react';

import type Maybe from 'graphql/tsutils/Maybe';
import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';

import Retracked from 'js/app/retracked';
import type { TrackingContextData } from 'js/lib/retracked';

import PlayIcon from 'bundles/promo-template/assets/PlayIcon';
import { eventingTransform } from 'bundles/promo-template/utils/eventingTransform';
import type { ImageType } from 'bundles/unified-common/components/Image';
import Image from 'bundles/unified-common/components/Image';

import _t from 'i18n!nls/promo-template';

export type VideoItem = {
  url?: Maybe<string>;
  contentType?: Maybe<string>;
};

type PropsFromCaller = {
  image: ImageType;
  video: VideoItem;
  videoTrackingName?: string;
  lazyLoadImage?: boolean;
  preloadImage?: boolean;
};

type PropsFromContext = {
  _eventData: TrackingContextData;
};

type Props = PropsFromCaller & PropsFromContext;

const styles = {
  container: css`
    position: relative;
    width: 100%;
    height: 100%;
  `,
  videoContent: (playVideo: 1 | 0) => css`
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: ${playVideo};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `,
  imageContainer: (showImage: boolean) => css`
    position: relative;
    z-index: ${showImage ? 0 : -1};
    opacity: ${showImage ? 1 : 0};
  `,
  playIcon: css`
    position: absolute;
    left: 50%;
    top: 50%;
    margin-left: -45px;
    margin-top: -45px;
  `,
  playMask: css`
    position: absolute;
    background-color: rgb(0 0 0 / 25%);
    transition: background-color 0.2s ease-in-out;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &:hover {
      background: rgb(0 0 0 / 50%);
    }
  `,
};

export function Video({
  image,
  video,
  _eventData,
  videoTrackingName: trackingNameFromProps,
  lazyLoadImage,
  preloadImage,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playButtonPressed, setPlayButtonPressed] = React.useState(false);

  if (!image.url || !video.url) {
    return null;
  }

  const handleOpenVideoPlayer = () => {
    if (video.url) {
      const videoTrackingName = trackingNameFromProps ?? eventingTransform(`video_${video.url}`);
      Retracked.trackComponent(
        _eventData,
        {
          videoUrl: video.url,
        },
        videoTrackingName,
        'play'
      );
    }
    setPlayButtonPressed(true);
    videoRef?.current?.play();
    return false;
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleOpenVideoPlayer();
    }
  };

  return (
    <div css={styles.container}>
      {/* Opacity 0 until user clicked custom overlay below */}
      <div data-testid="video_content" css={styles.videoContent(playButtonPressed ? 1 : 0)}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video tabIndex={playButtonPressed ? 0 : -1} controls width="100%" controlsList="nodownload" ref={videoRef}>
          {video.url && <source src={video.url} type={video.contentType ?? undefined} />}
        </video>
      </div>
      {/* Overlay with custom play button */}
      <div data-testid="cover_image" css={styles.imageContainer(!playButtonPressed)}>
        <Image {...image} shouldLazyLoad={lazyLoadImage} shouldPreload={preloadImage} />
        <div
          role="button"
          css={styles.playMask}
          tabIndex={0}
          onClick={handleOpenVideoPlayer}
          onKeyPress={handleKeyPress}
          aria-label={_t('Play video')}
        >
          <PlayIcon css={styles.playIcon} />
        </div>
      </div>
    </div>
  );
}

export default compose<Props, PropsFromCaller>(
  getContext({
    _eventData: PropTypes.object,
  })
)(Video);
